import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Button, 
  Box, 
  TextField,
  Alert
} from '@mui/material';
import { createColabfoldJob } from '@/api/jobs';
import { ColabfoldParams } from '@/types/colabfold';
import { ColabfoldForm } from '@/components/ModelForms';
import { useAwsSettings } from '@/hooks/useAwsSettings';
import JobFormHeader from '@/components/JobFormHeader';
import { ModelType } from '@/types/colabfold';

const ColabfoldTool: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isComplete: hasCompleteAwsSettings, loading: awsSettingsLoading } = useAwsSettings();
  const [formData, setFormData] = useState<ColabfoldParams>({
    jobname: `Colabfold-${new Date().toISOString().split('T')[0]}`,
    query_sequence: '',
    template_mode: 'none',
    num_recycles: 3,
    max_recycles: 20,
    num_relax: 0,
    msa_mode: 'single_sequence',
    pair_mode: 'unpaired',
    rank_by: 'plddt',
    stop_at_score: 100,
    num_models: 1,
    folder: location.state?.folderId || null
  });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      await createColabfoldJob(formData);
      navigate('/jobs'); // Navigate back to the all jobs list after submission
    } catch (error) {
      console.error('Error creating ColabFold job:', error);
      setError('Failed to submit job. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/jobs'); // Navigate back to the all jobs list
  };

  return (
    <Container>
      <Box p={3}>
        <JobFormHeader modelType={ModelType.COLABFOLD} />
        <Box mb={3}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {!hasCompleteAwsSettings && !awsSettingsLoading && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Your AWS settings are incomplete. Please complete your AWS settings before submitting a job.
            </Alert>
          )}
          
          <TextField
            fullWidth
            label="Job Name"
            value={formData.jobname}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
              setFormData({...formData, jobname: e.target.value})
            }
            required
            sx={{ mb: 3 }}
          />
          
          <ColabfoldForm 
            params={formData}
            onChange={setFormData}
          />
          
          <Box display="flex" justifyContent="flex-end" mt={3}>
            <Button 
              variant="outlined" 
              onClick={handleCancel} 
              sx={{ mr: 2 }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleSubmit}
              disabled={!formData.jobname || !formData.query_sequence || !hasCompleteAwsSettings || submitting || awsSettingsLoading}
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

// Styled components
const Container = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

export default ColabfoldTool; 